/**
 * Ticket WK5: dünne Hüllen der Landesseiten.
 *
 * Der öffentliche Weg (`loadWahlkalenderLandesseite`) hat bewusst keine
 * Anmeldeprüfung: die Seite wird beim Ausliefern auf dem Server erzeugt, wo es
 * kein Zugangsmerkmal gibt. Er liefert nur freigeschaltete Bundesländer.
 * Die Pflegewege sind Redaktion und Geschäftsführung vorbehalten.
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { BUNDESLAENDER } from "@/lib/contacts";
import type {
  LandesseiteAdminRow,
  PublicLandesseite,
} from "@/lib/wahlkalender-landesseite.server";

const sectionSchema = z.object({
  heading: z.string().trim().max(200),
  paragraphs: z.array(z.string().trim().max(4000)).max(30),
});

export const loadWahlkalenderLandesseite = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z.object({ slug: z.string().trim().min(1).max(60) }).parse(input),
  )
  .handler(async ({ data }): Promise<PublicLandesseite | null> => {
    const { loadPublicLandesseiteOnServer } = await import("@/lib/wahlkalender-landesseite.server");
    return loadPublicLandesseiteOnServer(data.slug);
  });

export const loadWahlkalenderDraft = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ slug: z.string().trim().min(1).max(60) }).parse(input))
  .handler(async ({ context, data }): Promise<PublicLandesseite | null> => {
    const { loadDraftLandesseiteOnServer } = await import("@/lib/wahlkalender-landesseite.server");
    return loadDraftLandesseiteOnServer(context.supabase, context.userId, data.slug);
  });

export const listWahlkalenderLandesseiten = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<LandesseiteAdminRow[]> => {
    const { listLandesseitenForAdminOnServer } = await import(
      "@/lib/wahlkalender-landesseite.server"
    );
    return listLandesseitenForAdminOnServer(context.supabase, context.userId);
  });

export const saveWahlkalenderLandesseite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        bundesland: z.enum(BUNDESLAENDER as unknown as [string, ...string[]]),
        published: z.boolean(),
        heading: z.string().trim().max(200),
        seoTitle: z.string().trim().max(200),
        seoDescription: z.string().trim().max(400),
        introParagraphs: z.array(z.string().trim().max(4000)).max(30),
        sections: z.array(sectionSchema).max(30),
        directoryHeading: z.string().trim().max(200),
        directoryIntro: z.string().trim().max(1000),
        shareImageUrl: z.string().trim().max(600).nullable().default(null),
      })
      .parse(input),
  )
  .handler(async ({ context, data }): Promise<{ ok: true }> => {
    const { saveLandesseiteOnServer } = await import("@/lib/wahlkalender-landesseite.server");
    await saveLandesseiteOnServer(context.supabase, context.userId, data as never);
    return { ok: true };
  });

/**
 * Freigabe 22.09.2026: Bundesländer mit freigeschalteter Landesseite – Grundlage
 * der Einträge je Bundesland in der Downloads-Übersicht. Öffentlich lesbar, es
 * werden nur Bundeslandschlüssel geliefert, keine Inhalte.
 */
export const listPublishedWahlkalenderBundeslaender = createServerFn({ method: "GET" }).handler(
  async (): Promise<Array<{ bundesland: string; shareImageUrl: string | null }>> => {
    const { listPublicLandesseitenPathsOnServer } = await import(
      "@/lib/wahlkalender-landesseite.server"
    );
    const rows = await listPublicLandesseitenPathsOnServer();
    return rows.map((row) => ({ bundesland: row.bundesland, shareImageUrl: row.shareImageUrl }));
  },
);
